// The UGI team can't ingest emojis or non-Latin characters, and this experience targets
// English-speaking guests, so beverage free text is restricted to plain ASCII: English
// letters, digits, whitespace and standard keyboard punctuation.
const ALLOWED_CHARACTERS = "\\t\\n\\r\\u0020-\\u007E";
const ALLOWED_TEXT_PATTERN = new RegExp(`^[${ALLOWED_CHARACTERS}]*$`);
const DISALLOWED_CHARACTER_PATTERN = new RegExp(`[^${ALLOWED_CHARACTERS}]`, "g");

// Spaces guests can't see (e.g. the narrow no-break space macOS/iOS insert in French text)
// become a plain space, and zero-width characters are dropped, so they never block saving.
const SPACE_LIKE_PATTERN = /[\u00A0\u2000-\u200A\u202F\u205F\u3000]/g;
const ZERO_WIDTH_PATTERN = /[\u200B-\u200D\u2060\uFEFF]/g;

// Smart punctuation (on by default on iOS/macOS) falls outside ASCII, so it's swapped
// for its ASCII equivalent instead of blocking guests who typed a plain apostrophe or quote.
const SMART_PUNCTUATION_REPLACEMENTS = [
  [/[\u2018\u2019\u201A\u2032]/g, "'"],
  [/[\u201C\u201D\u201E\u2033]/g, '"'],
  [/[\u2010-\u2015]/g, "-"],
  [/\u2026/g, "..."],
];

export const DISALLOWED_CHARACTERS_MESSAGE =
  "Please remove emojis and special characters before trying to save.";

// Accents are dropped rather than blocked (iOS autocorrects "cafe" to "café"): NFD splits
// "é" into "e" + a combining mark, which is then removed. Letters with no ASCII base
// (e.g. "ß", "ø") are unaffected and still count as disallowed.
const COMBINING_MARK_PATTERN = /[\u0300-\u036F]/g;

const normalizeBeverageText = (value) =>
  SMART_PUNCTUATION_REPLACEMENTS.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    value
      .normalize("NFD")
      .replace(COMBINING_MARK_PATTERN, "")
      .replace(SPACE_LIKE_PATTERN, " ")
      .replace(ZERO_WIDTH_PATTERN, ""),
  );

export const hasDisallowedCharacters = (value) => {
  if (typeof value !== "string" || !value) {
    return false;
  }

  return !ALLOWED_TEXT_PATTERN.test(normalizeBeverageText(value));
};

// Server-side counterpart of hasDisallowedCharacters: the UI blocks these characters, but
// older cached bundles and direct calls to the public action can still send them.
export const stripDisallowedCharacters = (value) => {
  if (typeof value !== "string" || !value) {
    return "";
  }

  return normalizeBeverageText(value).replace(DISALLOWED_CHARACTER_PATTERN, "");
};
